import { TMUA_PEARSON_TOKENS } from '../constants/tmuaPearsonTokens';

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

export function getMostLeastRestWidth(t = TMUA_PEARSON_TOKENS) {
  return t.mlLabelRestWidth + t.mlLabelBarGap + t.mlBarRestWidth;
}

export function getMostLeastCardRestWidth(t = TMUA_PEARSON_TOKENS) {
  return getMostLeastRestWidth(t) - t.mlTrayIndent - 2 * t.mlTrayPad;
}

export function getMostLeastShrinkWidths(availablePx, t = TMUA_PEARSON_TOKENS) {
  const rest = getMostLeastRestWidth(t);
  const cardRestWidth = getMostLeastCardRestWidth(t);
  const chromeShrink = 2 * (t.mlLabelRestWidth - t.mlLabelMinWidth);
  const deficit = Math.max(0, rest - availablePx);
  const labelWidth = clamp(
    t.mlLabelRestWidth - deficit / 2,
    t.mlLabelMinWidth,
    t.mlLabelRestWidth,
  );
  const emptyBarWidth = clamp(
    t.mlBarRestWidth - deficit / 2,
    t.mlBarMinWidth,
    t.mlBarRestWidth,
  );
  const extra = Math.max(0, deficit - chromeShrink);
  const cardWidth = Math.max(0, cardRestWidth - extra);
  const filledBarWidth = Math.max(0, t.mlBarRestWidth - extra);

  const chromeSpan = t.mlLabelRestWidth - t.mlLabelMinWidth;
  const labelHeight = chromeSpan === 0
    ? t.mlLabelRestHeight
    : Math.round(lerp(
      labelWidth,
      t.mlLabelRestWidth,
      t.mlLabelMinWidth,
      t.mlLabelRestHeight,
      t.mlLabelMinHeight,
    ));
  const emptyBarHeight = chromeSpan === 0
    ? t.mlBarRestHeight
    : Math.round(lerp(
      emptyBarWidth,
      t.mlBarRestWidth,
      t.mlBarMinWidth,
      t.mlBarRestHeight,
      t.mlBarMinHeight,
    ));

  // Height is a floor for min-height. Boxes grow with wrapped text; extra
  // length is what vertical scroll is for. Do not lerp height down with width.
  const cardHeight = t.mlCardRestHeight;
  const filledBarHeight = cardHeight;

  return {
    labelWidth,
    labelHeight,
    emptyBarWidth,
    emptyBarHeight,
    filledBarWidth,
    filledBarHeight,
    cardWidth,
    cardHeight,
    cardHeightFloor: cardHeight,
  };
}

function lerp(value, inStart, inEnd, outStart, outEnd) {
  if (inStart === inEnd) return outStart;
  return outStart + ((value - inStart) / (inEnd - inStart)) * (outEnd - outStart);
}

function roundTo(value, decimals) {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

export function getMostLeastTypeScale({ labelWidth, cardWidth }, t = TMUA_PEARSON_TOKENS) {
  const cardRestWidth = getMostLeastCardRestWidth(t);
  const cardFloorWidth = Math.round(
    (cardRestWidth * t.mlLabelMinWidth) / t.mlLabelRestWidth,
  );

  const labelFontSize = Math.round(
    clamp(
      lerp(
        labelWidth,
        t.mlLabelRestWidth,
        t.mlLabelMinWidth,
        t.mlLabelFontRest,
        t.mlLabelFontFloor,
      ),
      t.mlLabelFontFloor,
      t.mlLabelFontRest,
    ),
  );

  const cardFontSize = Math.round(
    clamp(
      lerp(
        cardWidth,
        cardRestWidth,
        cardFloorWidth,
        t.mlCardFontRest,
        t.mlCardFontFloor,
      ),
      t.mlCardFontFloor,
      t.mlCardFontRest,
    ),
  );

  const cardLineHeight = roundTo(
    clamp(
      lerp(
        cardWidth,
        cardRestWidth,
        cardFloorWidth,
        t.mlCardLineHeightRest,
        t.mlCardLineHeightFloor,
      ),
      t.mlCardLineHeightFloor,
      t.mlCardLineHeightRest,
    ),
    2,
  );

  return { labelFontSize, cardFontSize, cardLineHeight };
}
